import { PieChart, Pie, Tooltip, Cell, ResponsiveContainer } from "recharts";
import { useState } from "react";

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0];
    return (
      <div className="bg-black shadow-lg text-white border border-gray-100 rounded-lg p-3 text-xs">
        <div className="flex items-center gap-2">
          <span className="font-semibold">{data.name}</span>
        </div>
        <div className="">Count: {data.value}</div>
      </div>
    );
  }
  return null;
};

export default function StockMatchSummary({ matchedStocks, unmatchedStocks }) {
  const [showAllUnmatched, setShowAllUnmatched] = useState(false);
  const [showAllMatched, setShowAllMatched] = useState(false);
  const COLORS = ["#34C046", "#FF5050"];
  let totalStocksLength = matchedStocks.length + unmatchedStocks.length;
  const matchPercentage =
    totalStocksLength > 0
      ? (matchedStocks.length / totalStocksLength) * 100
      : 0;
  const chartData = [
    { name: "Matched", value: matchedStocks.length },
    { name: "Unmatched", value: unmatchedStocks.length },
  ];

  const visibleUnmatched = showAllUnmatched
    ? unmatchedStocks
    : unmatchedStocks.slice(0, 5);

  const visibleMatched = showAllMatched
    ? matchedStocks
    : matchedStocks.slice(0, 5);

  return (
    <div className="bg-white font-medium rounded-2xl shadow p-6 border text-black mx-auto">
      <h3 className="text-md font-semibold mb-6 text-center sm:text-left">
        Stock Matching Summary
      </h3>

      <div className="flex flex-col sm:flex-row items-start justify-between gap-8">
        {/* Pie Chart */}
        <div className="w-full sm:w-1/3 relative max-w-xs mx-auto">
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={chartData}
                dataKey="value"
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={75}
                paddingAngle={4}
                stroke="none"
                isAnimationActive={true}
                animationDuration={800}
                animationEasing="ease-out"
                label={({
                  cx,
                  cy,
                  midAngle,
                  innerRadius,
                  outerRadius,
                  percent,
                  name,
                  index,
                }) => {
                  const RADIAN = Math.PI / 180;
                  const radius = outerRadius + 20; // move labels outside the pie
                  const x = cx + radius * Math.cos(-midAngle * RADIAN);
                  const y = cy + radius * Math.sin(-midAngle * RADIAN);

                  // Pick color from COLORS array by index
                  const color = COLORS[index % COLORS.length];

                  return (
                    <text
                      x={x}
                      y={y}
                      fill={color} // Use matching color
                      fontSize={9}
                      fontWeight="600"
                      textAnchor={x > cx ? "start" : "end"}
                      dominantBaseline="central"
                    >
                      {`${name} ${(percent * 100).toFixed(0)}%`}
                    </text>
                  );
                }}
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                    cursor="pointer"
                    style={{ transition: "transform 0.3s ease" }}
                  />
                ))}
              </Pie>

              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>

          {/* Center Label */}
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-xs font-bold text-gray-700">
              {matchPercentage.toFixed(0)}%
            </span>
            <span className="text-xs text-gray-500">Matched</span>
          </div>
        </div>

        {/* Lists */}
        <div className="flex flex-col sm:flex-row gap-6 w-full sm:w-2/3">
          {/* Matched Stocks */}
          <div className="flex-1">
            <h4 className="text-md font-semibold text-[#34C046] mb-3">
              Matched Stocks ({matchedStocks.length})
            </h4>

            {matchedStocks.length === 0 ? (
              <div className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded p-4 text-center">
                No match found
              </div>
            ) : (
              <>
                <ul className="max-h-60 overflow-y-auto space-y-2 border border-[#34C046]/30 rounded p-3 bg-[#E8F6EC]">
                  {visibleMatched.map((stock) => (
                    <li
                      key={stock.asx_code}
                      className="p-2 bg-white rounded shadow-sm hover:shadow-md transition-shadow cursor-default"
                    >
                      {/* Top: Full-width stock name */}
                      <div className="text-sm font-semibold text-black mb-1">
                        {stock.name}
                      </div>

                      {/* Bottom: Code + Quantity on left, Matched % on right */}
                      <div className="flex justify-between items-start text-xs text-gray-600">
                        <div>
                          <div>
                            Code:{" "}
                            <span className="text-gray-800">
                              {stock.asx_code}
                            </span>
                          </div>
                          <div>
                            Quantity:{" "}
                            <span className="text-gray-800">{stock.user_quantity}</span>
                          </div>
                        </div>
                        <div className="text-right text-gray-600 min-w-[60px]">
                          Matched %:{" "}
                          <span className="text-[#34C046] font-semibold">
                            {stock.strategy_percentage
                              ? stock.strategy_percentage.toFixed(2)
                              : "0.00"}
                          </span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>

                {matchedStocks.length > 5 && (
                  <button
                    onClick={() => setShowAllMatched(!showAllMatched)}
                    className="mt-2 text-[#005A9C] hover:underline text-xs font-medium"
                  >
                    {showAllMatched ? "Show Less" : "View All"}
                  </button>
                )}
              </>
            )}
          </div>

          {/* Unmatched Stocks */}
          <div className="flex-1">
            <h4 className="text-md font-semibold text-[#FF5050] mb-3">
              Unmatched Stocks ({unmatchedStocks.length})
            </h4>

            {unmatchedStocks.length === 0 ? (
              <div className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded p-4 text-center">
                No unmatched stocks found
              </div>
            ) : (
              <>
                <ul className="max-h-60 overflow-y-auto space-y-2 border border-[#FF5050]/30 rounded p-3 bg-[#FDECEC]">
                  {visibleUnmatched.map((stock) => (
                    <li
                      key={stock.asx_code}
                      className="p-2 bg-white rounded shadow-sm hover:shadow-md transition-shadow cursor-default"
                    >
                      {/* Top: Full-width stock name */}
                      <div className="text-sm font-semibold text-black mb-1">
                        {stock.name}
                      </div>

                      {/* Bottom: Code + Quantity on left */}
                      <div className="flex justify-between items-start text-xs text-gray-600">
                        <div>
                          <div>
                            Code:{" "}
                            <span className="text-gray-800">
                              {stock.asx_code}
                            </span>
                          </div>
                          <div>
                            Quantity:{" "}
                            <span className="text-gray-800">{stock.user_quantity}</span>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>

                {unmatchedStocks.length > 5 && (
                  <button
                    onClick={() => setShowAllUnmatched(!showAllUnmatched)}
                    className="mt-2 text-[#005A9C] hover:underline text-xs font-medium"
                  >
                    {showAllUnmatched ? "Show Less" : "View All"}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
